import { ApplicationConfig, ErrorHandler, LOCALE_ID, provideBrowserGlobalErrorListeners } from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideServiceWorker } from '@angular/service-worker';
import { provideAuth } from 'angular-auth-oidc-client';

import { routes } from './app.routes';
import { environment } from '../environments/environment';
import { authInterceptor } from './core/auth/interceptors/auth.interceptor';
import { connectivityInterceptor } from './core/http/connectivity.interceptor';
import { requestContextInterceptor } from './core/http/request-context.interceptor';
import { authInitializerProvider } from './core/auth/auth-config.factory';
import { provideEnvironmentNgxMask } from 'ngx-mask';
import { MAT_DATE_LOCALE, provideNativeDateAdapter } from '@angular/material/core';
import { provideEchartsCore } from 'ngx-echarts';

import { QUALIFICACAO_PESSOA_TOKEN } from './core/qualificacao-pessoa/qualificacao-pessoa.token';
import { QualificacaoPessoaServiceImpl } from './features/consultas/pessoa/services/qualificacao-pessoa.service';
import { NxIconService } from './core/services/icon.service';
import { GlobalErrorHandler } from './core/error-handling/global-error-handler';

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideRouter(routes),
    provideHttpClient(
      withInterceptors([requestContextInterceptor, authInterceptor, connectivityInterceptor])
    ),
    provideEnvironmentNgxMask({
      // Configuração para usar em múltiplos lugares
      validation: true,                   // Habilita validação
      showMaskTyped: false,               // Mostra máscara enquanto digita
      dropSpecialCharacters: true,        // Remove caracteres especiais do valor
      clearIfNotMatch: false,             // Limpa input se não coincidir
      thousandSeparator: '.',             // Separador de milhares (padrão BR)
      decimalMarker: ',',                 // Marcador decimal (padrão BR)
      allowNegativeNumbers: false,        // Permite números negativos
      leadZeroDateTime: false,            // Zero à esquerda em data/hora
      placeHolderCharacter: '_',          // Caractere do placeholder
      prefix: '',                         // Prefixo padrão
      suffix: '',                         // Sufixo padrão
      specialCharacters: [                // Caracteres especiais permitidos
        '-', '/', '(', ')', '.', ':', ' ', 
        '+', ',', '@', '[', ']', '"', "'"
      ]
    }),
    provideNativeDateAdapter(),
    { provide: MAT_DATE_LOCALE, useValue: 'pt-BR' },
    { provide: LOCALE_ID, useValue: 'pt-BR' },
    provideEchartsCore({ echarts: () => import('echarts') }),
    provideServiceWorker('ngsw-worker.js', {
      enabled: environment.serviceWorkerEnabled,
      registrationStrategy: 'registerWhenStable:30000',
    }),
    authInitializerProvider,
    provideAuth({
      config: {
        authority: `${environment.keycloak.issuer}/realms/${environment.keycloak.realm}`,
        redirectUrl: `${window.location.origin}/auth-callback`,
        postLogoutRedirectUri: window.location.origin,
        clientId: environment.keycloak.clientId,
        scope: 'openid profile email offline_access cpf matricula unidade unidade_cod title roles',
        responseType: 'code',
        silentRenew: true,
        silentRenewUrl: `${window.location.origin}/silent-renew.html`,
        useRefreshToken: true,
        renewTimeBeforeTokenExpiresInSeconds: 30,
        ignoreNonceAfterRefresh: true,
        triggerAuthorizationResultEvent: true,
        logLevel: environment.features.enableDebugTools ? 2 : 0,
        autoUserInfo: false,
        disableIdTokenValidation: false,
        disableRefreshIdTokenAuthTimeValidation: true,
        maxIdTokenIatOffsetAllowedInSeconds: 600,
        // ⚠️ TEMPORÁRIO: Desabilita validação de issuer apenas em desenvolvimento
        // TODO: Remover quando o Keycloak for reconfigurado com Frontend URL correto
        // disableIssuerValidation: !environment.production,
        secureRoutes: [
          environment.apiNexus,
          environment.apiSisFrota,
          environment.apiPatrimonio,
          environment.atendimentoApi,
          environment.apiSigecad,
          environment.apiProvid,
          environment.apiVisus,
        ].filter((url): url is string => !!url),
      }
    }),
    { provide: QUALIFICACAO_PESSOA_TOKEN, useExisting: QualificacaoPessoaServiceImpl },
    NxIconService,
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
  ]
};
